import { useRef, useCallback, useEffect, useState } from 'react'
import { createLogger } from '../utils/logger'

const logger = createLogger('WebSocket')

export const CONNECTION_STATUS = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
}

/**
 * Consolidated WebSocket hook with connection management, reconnection, and message handling
 * Combines functionality from useWebSocketConnection, useWebSocketReconnect, and useWebSocketMessageHandler
 * 
 * @param {object} config - Configuration object
 * @param {string} config.wsUrl - Base WebSocket URL
 * @param {string} config.channelId - Channel identifier (optional)
 * @param {function} config.onMessage - Callback for messages
 * @param {function} config.onError - Optional error callback
 * @param {function} config.onOpen - Optional open callback
 * @param {function} config.onClose - Optional close callback
 * @param {number} config.maxReconnectAttempts - Maximum reconnection attempts (default: 10)
 * @param {number} config.baseDelay - Base delay in ms for reconnection (default: 1000)
 * @param {boolean} config.autoConnect - Automatically connect on mount (default: true)
 * @returns {object} Connection state and utilities
 */
export default function useWebSocket(config) {
  const {
    wsUrl,
    channelId,
    onMessage,
    onError,
    onOpen,
    onClose,
    maxReconnectAttempts = 10,
    baseDelay = 1000,
    autoConnect = true
  } = config

  // State
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.DISCONNECTED)

  // Refs
  const wsRef = useRef(null)
  const channelIdRef = useRef(channelId)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)

  // Update channelId ref when it changes
  useEffect(() => {
    channelIdRef.current = channelId
  }, [channelId])

  // Update connection status when reconnecting
  useEffect(() => {
    if (reconnectAttemptsRef.current > 0 && connectionStatus === CONNECTION_STATUS.DISCONNECTED) {
      setConnectionStatus(CONNECTION_STATUS.RECONNECTING)
    }
  }, [reconnectAttemptsRef.current, connectionStatus])

  // Clear reconnect timeout
  const clearReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  // Reset reconnect attempts
  const resetAttempts = useCallback(() => {
    reconnectAttemptsRef.current = 0
  }, [])

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      logger.error(`[WebSocket] Max reconnection attempts reached (${maxReconnectAttempts})`, {
        channelId,
        timestamp: new Date().toISOString()
      })
      return false
    }

    const delay = Math.min(
      baseDelay * Math.pow(2, reconnectAttemptsRef.current),
      30000 // Max 30 seconds
    )

    logger.info(`[WebSocket] Scheduling reconnection attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts}`, {
      channelId,
      delay,
      nextAttempt: reconnectAttemptsRef.current + 1,
      timestamp: new Date().toISOString()
    })

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++
      connect()
    }, delay)

    return true
  }, [channelId, maxReconnectAttempts, baseDelay])

  // Handle incoming messages
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data)
      const messageData = message.data

      // Route messages based on type
      switch (message.type) {
        case 'new_message':
          if (onMessage && messageData) {
            onMessage(messageData)
          }
          break

        case 'message_edited':
          if (onMessage && messageData) {
            onMessage({ ...messageData, isEdited: true })
          }
          break

        case 'message_deleted':
          if (onMessage && messageData) {
            onMessage({ ...messageData, isDeleted: true })
          }
          break

        case 'typing':
          // Future: Handle typing indicators
          break

        case 'error':
          logger.error('Server error', messageData)
          if (onError) onError(messageData)
          break

        default:
          logger.warn('Unknown message type', { type: message.type })
      }
    } catch (error) {
      logger.error('Failed to parse message', error)
    }
  }, [onMessage, onError])

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!channelId) return
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    try {
      const fullWsUrl = `${wsUrl}/api/channels/${channelId}/ws`
      logger.info(`[WebSocket] Initiating connection to: ${fullWsUrl}`, {
        channelId,
        wsUrl: fullWsUrl,
        reconnectAttempt: reconnectAttemptsRef.current,
        timestamp: new Date().toISOString()
      })

      setConnectionStatus(CONNECTION_STATUS.CONNECTING)

      const ws = new WebSocket(fullWsUrl)
      ws._channelId = channelId
      wsRef.current = ws

      logger.info(`[WebSocket] WebSocket object created, readyState: ${ws.readyState}`, {
        channelId,
        readyState: ws.readyState
      })

      // Handle connection open
      ws.onopen = () => {
        logger.info(`[WebSocket] Connection opened successfully`, {
          channelId,
          wsUrl: fullWsUrl,
          timestamp: new Date().toISOString()
        })
        setConnectionStatus(CONNECTION_STATUS.CONNECTED)
        resetAttempts()
        if (onOpen) onOpen()
      }

      // Handle incoming messages
      ws.onmessage = handleMessage

      // Handle errors
      ws.onerror = (error) => {
        logger.error(`[WebSocket] Error occurred`, {
          channelId,
          wsUrl: fullWsUrl,
          readyState: ws.readyState,
          timestamp: new Date().toISOString()
        })

        if (ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
          setConnectionStatus(CONNECTION_STATUS.ERROR)
          if (onError) onError(error)
        }
      }

      // Handle connection close
      ws.onclose = (event) => {
        const logData = {
          channelId,
          wsUrl: fullWsUrl,
          code: event.code,
          reason: event.reason || '(no reason provided)',
          wasClean: event.wasClean,
          reconnectAttempts: reconnectAttemptsRef.current,
          maxReconnectAttempts,
          timestamp: new Date().toISOString()
        }

        if (event.code === 1000) {
          logger.info(`[WebSocket] Connection closed (normal)`, logData)
        } else {
          logger.warn(`[WebSocket] Connection closed (abnormal)`, logData)
        }

        wsRef.current = null
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED)

        if (onClose) onClose(event)

        // Only attempt reconnect if close wasn't intentional
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          scheduleReconnect()
        }
      }
    } catch (error) {
      logger.error('Failed to create connection', error)
      setConnectionStatus(CONNECTION_STATUS.ERROR)
      if (onError) onError(error)
    }
  }, [channelId, wsUrl, handleMessage, onOpen, onError, onClose, maxReconnectAttempts, resetAttempts, scheduleReconnect])

  // Disconnect from WebSocket
  const disconnect = useCallback((targetChannelId) => {
    const currentChannelId = channelIdRef.current
    const wsChannelId = wsRef.current?._channelId

    logger.info('[WebSocket] Manual disconnect called', {
      channelId: currentChannelId,
      targetChannelId: targetChannelId || '(any)',
      wsChannelId: wsChannelId || '(none)',
      hasActiveConnection: !!wsRef.current,
      readyState: wsRef.current?.readyState,
      timestamp: new Date().toISOString()
    })

    // If targetChannelId is specified, only disconnect if it matches
    if (targetChannelId && wsChannelId && wsChannelId !== targetChannelId) {
      logger.info('[WebSocket] Skipping disconnect - connection is for different channel', {
        wsChannelId,
        targetChannelId
      })
      return
    }

    clearReconnect()

    if (wsRef.current) {
      const ws = wsRef.current
      const readyState = ws.readyState

      // If still connecting, wait before closing
      if (readyState === WebSocket.CONNECTING) {
        logger.info('[WebSocket] Connection still establishing, deferring close', {
          channelId: currentChannelId,
          readyState
        })

        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close(1000, 'Client disconnecting')
          }
        }, 100)

        wsRef.current = null
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED)
        return
      }

      // For OPEN connections, close normally
      if (readyState === WebSocket.OPEN) {
        ws.close(1000, 'Client disconnecting')
      }
      
      wsRef.current = null
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED)
    }
  }, [clearReconnect])

  // Send message over WebSocket
  const sendMessage = useCallback((type, payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }))
      return true
    }
    logger.warn('[WebSocket] Cannot send message - connection not open', {
      channelId: channelIdRef.current,
      readyState: wsRef.current?.readyState
    })
    return false
  }, [])

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (!autoConnect || !channelId) return

    const previousChannelId = channelIdRef.current
    
    if (previousChannelId && previousChannelId !== channelId) {
      // Switching channels - disconnect from old one first
      disconnect(previousChannelId)
    }

    resetAttempts()
    connect()

    return () => {
      clearReconnect()
      disconnect(channelId)
    }
  }, [channelId, autoConnect])

  return {
    connectionStatus,
    isConnected: connectionStatus === CONNECTION_STATUS.CONNECTED,
    isConnecting: connectionStatus === CONNECTION_STATUS.CONNECTING || connectionStatus === CONNECTION_STATUS.RECONNECTING,
    connect,
    disconnect,
    sendMessage,
    reconnect: connect
  }
}
